/**
 * PersistentWorkspaceToggleRow
 *
 * "Keep this workspace" — an opt-in toggle in the AI terminal context bar's
 * expanded section (Task T16, AI Smoothness Wave, F1). On, the connection's AI
 * workspace lives in a fixed `<connection>_persistent` folder that survives
 * session close and app restart, so an agent's notes and `.history/` are still
 * there next time. Off (the default) keeps today's behaviour: a throwaway
 * numbered folder, wiped on close.
 *
 * Extracted into its own file per the ≤200-line-file guidance rather than
 * growing `AiTerminalContextBar.tsx` — the same call T15 made for
 * `AddSkillNudgeRow`.
 *
 * Two writes per toggle, in this order:
 *  1. the renderer-persisted registry (`aiTerminalSettings.persistentWorkspaces`),
 *     which is what `createWorkspace` consults when the NEXT AI terminal opens;
 *  2. the folder's on-disk `.jam-persistent` marker, which is what startup
 *     cleanup consults — main receives settings only after the renderer boots,
 *     i.e. strictly after the startup wipe has already run.
 *
 * If (2) fails the folder is simply kept a while longer (turning the toggle
 * off leaves a marker behind, so cleanup skips the folder until the next
 * successful call). Nothing is ever deleted as a consequence of a failed call.
 */

import { Switch } from '../../../components/ui/switch';
import { useAppStore } from '../../../store/appStore';
import { persistentWorkspaceFolderName } from '../../../types/aiTerminalSettings';
import { useWorkspacePersistence } from '../hooks/useWorkspacePersistence';
import type { TerminalSession } from '../../../types/terminal';

type PersistentWorkspaceToggleRowProps = {
  session: TerminalSession;
  /** Display name the folder is derived from — the connection's alias or server. */
  connectionName: string;
};

export function PersistentWorkspaceToggleRow({
  session,
  connectionName,
}: PersistentWorkspaceToggleRowProps) {
  const connectionId = session.connectionId;
  // `?.` so a store slice predating this section yields `undefined` (a stable
  // value — never a fresh `{}`, which would loop zustand) instead of throwing.
  const entry = useAppStore((s) =>
    connectionId ? s.aiTerminalSettings.persistentWorkspaces?.[connectionId] : undefined
  );
  const { setPersistence } = useWorkspacePersistence();

  if (!connectionId) return null;

  const enabled = !!entry;
  // Turning OFF must target the folder actually registered, not a fresh
  // derivation — otherwise a folder registered under an older connection name
  // would keep its marker forever.
  const folderName =
    entry?.folderName ?? persistentWorkspaceFolderName(connectionName, connectionId);
  const usingKeptFolderNow =
    !!session.workspacePath && session.workspacePath.split(/[\\/]/).pop() === folderName;

  // The switch is driven by the registry, and the hook rolls the registry back
  // when the disk write fails — so the control can never settle on a state the
  // folder does not actually have (I-4).
  const handleChange = (checked: boolean) => {
    void setPersistence({ connectionId, folderName, keep: checked });
  };

  return (
    <div
      className="mt-2 flex items-start justify-between gap-3 rounded border border-border/50 bg-background/50 p-2"
      data-testid="ai-workspace-persist-row"
    >
      <div className="min-w-0">
        <label
          htmlFor="ai-workspace-persist-toggle"
          className="cursor-pointer font-medium text-foreground"
        >
          Keep this workspace
        </label>
        <p className="mt-0.5 text-muted-foreground">
          {enabled ? (
            <>
              Reused as{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-foreground">{folderName}</code>
              . Agent notes and <code className="font-mono">.history/</code> are kept instead of deleted.
              {!usingKeptFolderNow && ' Takes effect the next time you open an AI terminal for this connection.'}
            </>
          ) : usingKeptFolderNow ? (
            // Turning it off mid-session is symmetric with turning it on: the
            // running session keeps the folder it was given, and the change
            // applies from the next one. Saying so beats silently deleting a
            // folder the user is still working in.
            <>
              This session keeps using{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-foreground">{folderName}</code>
              ; it is cleaned up the next time Jam SQL Studio starts.
            </>
          ) : (
            <>This workspace folder is deleted when the terminal closes.</>
          )}
        </p>
      </div>
      <Switch
        id="ai-workspace-persist-toggle"
        checked={enabled}
        onCheckedChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        data-testid="ai-workspace-persist-toggle"
      />
    </div>
  );
}
